import React, { useMemo } from 'react';
import { ErrorBoundary } from '@toss/error-boundary';
import { ICON_COLOR } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { ErrorBoundaryFallback } from '@/components';
import { useLocale } from '@/hooks';
import { ChangeIcon, MapFillIcon } from '@/icons';
import { type MapTemplate, MapTemplateTypeLocaleKey } from '@/types/MapTemplate';
import { cn } from '@/utils';
import { useBuildInfoContext } from '@/views/build/context';
import { useBuildFormContext } from '@/views/build/form/schema';
import { useShowMapTemplateModal } from '../../features/map-selection';
import { useFocusTarget } from '../focus/useFocusTarget';
import { ViewportResponsiveButton } from './ViewportResponsiveButton';
export const SelectMapTemplateField = () => {
  const {
    canEditQuiz,
    mapTemplates
  } = useBuildInfoContext();
  const {
    open
  } = useShowMapTemplateModal();
  const {
    watch
  } = useBuildFormContext();
  const selectedMapTemplateId = watch('templateId');
  const selectedMap = useMemo(() => mapTemplates?.find(m => m.templateId === selectedMapTemplateId), [mapTemplates, selectedMapTemplateId]);
  return !selectedMap ? <NotSelected handleMapTemplateModalShow={() => open({
    from: 'settings_page'
  })} isChangeEnabled={canEditQuiz} data-sentry-element="NotSelected" data-sentry-component="SelectMapTemplateField" data-sentry-source-file="SelectMapTemplateField.tsx" /> : <ErrorBoundary renderFallback={e => <ErrorBoundaryFallback {...e} />} data-sentry-element="ErrorBoundary" data-sentry-component="SelectMapTemplateField" data-sentry-source-file="SelectMapTemplateField.tsx">
      <Selected selectedMap={selectedMap} handleMapTemplateModalShow={() => open({
      from: 'settings_page',
      isChange: true
    })} isChangeEnabled={canEditQuiz} data-sentry-element="Selected" data-sentry-source-file="SelectMapTemplateField.tsx" />
    </ErrorBoundary>;
};
const NotSelected = (props: {
  handleMapTemplateModalShow: () => void;
  isChangeEnabled: boolean;
}) => {
  const {
    t
  } = useTranslation(['build']);
  const mapTemplateFieldRef = useFocusTarget<HTMLDivElement>('templateId');
  return <div ref={mapTemplateFieldRef} tabIndex={props.isChangeEnabled ? 0 : -1} role="button" aria-disabled={!props.isChangeEnabled} className={cn(['relative overflow-hidden rounded-md bg-strong-alternative', props.isChangeEnabled && 'cursor-pointer hover:bg-violet-100', !props.isChangeEnabled && 'cursor-default opacity-70', 'border border-opacity-assistive', 'h-[160px] w-full', 'flex justify-center pt-[36px]', 'focus:focus-ring'])} onClick={() => {
    if (props.isChangeEnabled) {
      props.handleMapTemplateModalShow();
    }
  }} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (props.isChangeEnabled) {
        props.handleMapTemplateModalShow();
      }
    }
  }} data-sentry-component="NotSelected" data-sentry-source-file="SelectMapTemplateField.tsx">
      <div className="absolute bottom-0 left-0 right-0 h-[54px]">
        <img src="/assets/map_image.webp" alt="Map preview" className="absolute bottom-0 left-1/2 h-full w-auto -translate-x-1/2 object-cover" />
      </div>

      {/* Center content */}
      <div className="flex flex-col items-center justify-center gap-3xs self-start">
        <div className="flex h-lg w-lg items-center justify-center">
          <MapFillIcon size="md" className="text-strong" data-sentry-element="MapFillIcon" data-sentry-source-file="SelectMapTemplateField.tsx" />
        </div>
        <span className="whitespace-nowrap text-heading-sm-bold text-strong">
          {t('build.map.select')}
        </span>
      </div>
    </div>;
};
const Selected = ({
  selectedMap,
  handleMapTemplateModalShow,
  isChangeEnabled
}: {
  selectedMap: MapTemplate;
  handleMapTemplateModalShow: () => void;
  isChangeEnabled: boolean;
}) => {
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    locale
  } = useLocale();
  return <div tabIndex={isChangeEnabled ? 0 : -1} role="button" aria-disabled={!isChangeEnabled} className={cn('group h-[76.6px] mobile-lg:h-[110px] overflow-hidden border-opacity-assistive bg-default rounded-[22px]', isChangeEnabled && 'cursor-pointer hover:bg-gray-50')} onClick={() => {
    if (isChangeEnabled) {
      handleMapTemplateModalShow();
    }
  }} onKeyDown={e => {
    if (!isChangeEnabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleMapTemplateModalShow();
    }
  }} data-sentry-component="Selected" data-sentry-source-file="SelectMapTemplateField.tsx">
      <div className="relative flex h-full gap-sm">
        {/* Map thumbnail */}
        <div className="relative shrink-0 border-2 border-transparent bg-transparent p-3xs group-hover:rounded-[22px] group-hover:border-opacity-black-3">
          <div className={cn('relative h-full')}>
            <div className={cn('items-center justify-center hidden absolute inset-0 bg-dimmed rounded-md', isChangeEnabled && 'group-hover:flex')}>
              <MapFillIcon size="xl" fill={ICON_COLOR.inverse} data-sentry-element="MapFillIcon" data-sentry-source-file="SelectMapTemplateField.tsx" />
            </div>
            <img className={cn('w-[99px] mobile-lg:w-[150px] object-cover rounded-md')} src={selectedMap?.thumbnailImage} alt={selectedMap?.serializedLocalize[locale]?.name || 'Map thumbnail'} />
          </div>
        </div>

        {/* Map info */}
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-3xs">
          <h3 className="truncate text-heading-sm-bold text-default">
            {selectedMap?.serializedLocalize?.[locale]?.name ?? selectedMap?.serializedLocalize?.en?.name ?? ''}
          </h3>
          <p className="text-body-sm-medium text-alternative">
            {selectedMap?.type && t(MapTemplateTypeLocaleKey[selectedMap.type])}
          </p>
        </div>

        {/* Change button */}
        {isChangeEnabled && <div className="flex items-center">
            <ViewportResponsiveButton className="gap-2xs text-body-sm-semibold" leftSlot={<ChangeIcon size="xs" fill={ICON_COLOR.neutral} />}>
              {t('common:global.change.button')}
            </ViewportResponsiveButton>
          </div>}
      </div>
    </div>;
};